import { Placer } from '../../components/OrgSwitcher'
import { mailboxWords, orgCountWords } from '../../lib/orgs'
import type { PasswordReset } from '../../types/account'
import type { OrgsView } from '../../types/orgs'

// Who is in which org, on the same screen as the settings that DEFINE the orgs — the aliases that
// fold two domains into one, and the names the firm gives them. Reading an org is nobody's privilege
// (the switcher in the sidebar does it for everyone); PLACING a person is an admin's, and the placer
// here is the same component the switcher uses.
//
// Placement is most of the people management on this screen. Nothing here deactivates an account or
// edits a person: an org is where somebody's pipeline goes, not who they are.
//
// The one exception is the password reset, and it is here because this is the only list of the firm's
// people an admin ever sees. It does not set anybody's password — an admin cannot know one — it mints
// a temporary one to hand over, shown once and never again.

export interface OrgsPanelProps {
  orgs: OrgsView
  /** the person whose placer is open */
  placing: string | null
  /** the person whose placement is in flight */
  pending: string | null
  error: string | null
  onPlacing: (userId: string | null) => void
  onPlace: (userId: string, key: string) => void
  /** resetting somebody's password is an admin's; for anyone else the action is absent, not refused */
  canReset?: boolean
  /** the person whose reset is in flight */
  resetting?: string | null
  /** the one copy of a freshly minted temporary password */
  reset?: PasswordReset | null
  resetError?: string | null
  onReset?: (userId: string) => void
}

export function OrgsPanel({ orgs, placing, pending, error, onPlacing, onPlace,
  canReset = false, resetting = null, reset = null, resetError = null, onReset }: OrgsPanelProps) {
  /** The reset action beside one person, and the temporary password when it is theirs. */
  function resetFor(userId: string) {
    if (!canReset || !onReset) return null
    return (
      <>
        <button type="button" className="link org-place" disabled={resetting === userId} onClick={() => onReset(userId)}>
          {resetting === userId ? 'Resetting…' : 'Reset password'}
        </button>
        {reset?.userId === userId && (
          <div className="temp-password" role="status">
            <code>{reset.temporaryPassword}</code>
            <p className="muted small">Give this to them; it is not shown again. They choose their own the first time they sign in.</p>
          </div>
        )}
      </>
    )
  }

  return (
    <section className="card setting-section orgs-panel">
      <div className="card-head">
        <div>
          <h2>Who is in which org</h2>
          <p className="muted">
            A person is placed by their connected mailbox, else by their sign-in domain. The people minted by name
            have neither, so somebody has to say — until then they see nothing, and the landscape tells them why.
          </p>
        </div>
      </div>

      <ul className="plain org-cards">
        {orgs.orgs.map((org) => (
          <li key={org.key} className="org-card">
            <div className="org-card-head">
              <strong>{org.name}</strong>
              {org.key === orgs.mine && <em className="org-yours">yours</em>}
              <span className="muted small">{org.domains.join(' · ')}</span>
            </div>
            <p className="muted small">{orgCountWords(org)} · {mailboxWords(org)}</p>
            <ul className="plain org-members">
              {org.members.map((m) => (
                <li key={m.userId}>
                  <span className="org-member-name">{m.displayName}</span>
                  {!m.hasMailbox && <span className="org-member-note">no mailbox</span>}
                  <Placer userId={m.userId} label="Move" current={org.key} orgs={orgs.orgs} placing={placing}
                    pending={pending} onPlacing={onPlacing} onPlace={onPlace} />
                  {resetFor(m.userId)}
                </li>
              ))}
              {org.members.length === 0 && <li className="muted small">Nobody here yet — the mailbox that named this org has no account.</li>}
            </ul>
          </li>
        ))}
      </ul>

      {orgs.unassigned.length > 0 && (
        <div className="org-unplaced">
          <p className="kicker">Not in an org yet</p>
          <ul className="plain org-members">
            {orgs.unassigned.map((p) => (
              <li key={p.userId}>
                <span className="org-member-name">{p.displayName}</span>
                <Placer userId={p.userId} label="Place" current={null} orgs={orgs.orgs} placing={placing}
                  pending={pending} onPlacing={onPlacing} onPlace={onPlace} />
                {resetFor(p.userId)}
              </li>
            ))}
          </ul>
          <p className="muted small">No mailbox and no sign-in domain to read, so nothing places them.</p>
        </div>
      )}

      {error && <p className="setting-refusal">{error}</p>}
      {resetError && <p className="setting-refusal">{resetError}</p>}
    </section>
  )
}
